// One thread: a dream, what it landed on, and the pipeline's reason for pairing them.
//
// Lifted out of the dashboard when the history screen appeared, so the two cannot drift. The rules it
// encodes are worth keeping in one place:
//
// Confidence is deliberately not shown. The pipeline only ever emits 0.8–0.9, so any indicator would
// look identical on every row and imply a precision that is not there.
//
// The connector is a border on the middle row rather than an absolutely positioned line, so it always
// spans exactly the gap between the two ends however either one wraps.

import { Link } from 'react-router-dom';
import { relationLabel, type ResolvedLink, type ThreadEnd } from '../api/links';

const SAME_YEAR = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const OTHER_YEAR = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * "Jul 6", or "Jul 6, 2025" once the year stops being obvious.
 *
 * The dashboard has always written these bare, which is right for a row that sits under today's date.
 * The history screen scrolls back through years, where a bare "Jul 6" quietly becomes a lie about
 * which July it was.
 */
function threadDate(date: Date): string {
  const thisYear = date.getFullYear() === new Date().getFullYear();
  return (thisYear ? SAME_YEAR : OTHER_YEAR).format(date);
}

export function ThreadRow({ thread }: { thread: ResolvedLink }) {
  return (
    <div className="othread">
      <ThreadNode
        end={{ kind: 'entry', entry: thread.from }}
        dot="is-from"
      />

      <div className="othread-rel">{relationLabel(thread.relation)}</div>

      <ThreadNode end={thread.to} dot="is-to" />

      <p className="othread-why">{thread.explanation}</p>
    </div>
  );
}

/**
 * One end of a thread.
 *
 * An entry end is a link and carries its date. A theme end is neither — it is a name the pipeline has
 * been following across many entries, with no single place to go and no single date to show. Giving it
 * a fake one of either would be inventing data on the screen whose whole promise is that it doesn't.
 */
function ThreadNode({ end, dot }: { end: ThreadEnd; dot: 'is-from' | 'is-to' }) {
  if (end.kind === 'theme') {
    return (
      <div className="othread-node">
        <span className={`othread-dot ${dot} is-theme`} />
        <span className="othread-title is-theme">{end.label}</span>
        <span className="othread-when">a theme you keep returning to</span>
      </div>
    );
  }

  const { entry } = end;
  return (
    <div className="othread-node">
      <span className={`othread-dot ${dot}`} />
      <Link className="othread-title" to={`/journal/${entry.id}`}>
        {entry.title}
      </Link>
      <span className="othread-when">
        {entry.kind === 'dream' ? 'dream' : 'waking'} · {threadDate(entry.createdAt)}
      </span>
    </div>
  );
}
